import type { I18nFormatModule, TOptions } from 'i18next';

// Every message in this app's catalog only substitutes `{name}` arguments, so this renders exactly that slice of
// ICU MessageFormat, the way intl-messageformat renders it. An apostrophe is plain text: ICU's quoting would turn the
// French `l'{name}` into `l{name}`. A message using any other ICU syntax (plural, select, number, ...) comes back as
// written, and test/locale-placeholder-syntax.test.ts keeps such messages, and ICU quoting, out of the catalog.
type MessagePart = { literal: string } | { argument: string };

const ARGUMENT = /\{([A-Za-z_][A-Za-z0-9_]*)\}/y;

/** The message's literal text and `{name}` arguments, or `null` when it uses ICU syntax beyond `{name}`. */
export function parseMessage(message: string): MessagePart[] | null {
  const parts: MessagePart[] = [];
  let literal = '';
  let index = 0;
  while (index < message.length) {
    const char = message[index];
    if (char === '{') {
      ARGUMENT.lastIndex = index;
      const argument = ARGUMENT.exec(message);
      if (!argument) return null;
      if (literal) parts.push({ literal });
      literal = '';
      parts.push({ argument: argument[1] });
      index = ARGUMENT.lastIndex;
    } else {
      literal += char;
      index += 1;
    }
  }
  if (literal) parts.push({ literal });
  return parts;
}

/**
 * The message with its arguments filled in from `values`. A message with an argument `values` lacks, or with
 * syntax beyond `{name}`, comes back as written.
 */
export function formatMessage(message: string, values?: Record<string, unknown>): unknown {
  const parts = typeof message === 'string' ? parseMessage(message) : null;
  if (!parts) return message;
  const rendered: unknown[] = [];
  for (const part of parts) {
    if ('literal' in part) {
      rendered.push(part.literal);
      continue;
    }
    if (!(values && part.argument in values)) return message;
    const value = values[part.argument];
    if (typeof value === 'string' || typeof value === 'number') rendered.push(String(value));
    else rendered.push(value || '');
  }
  if (rendered.length === 1) return rendered[0];
  return rendered.reduce<string>((all, part) => all + (part as string), '');
}

export const messageFormat: I18nFormatModule & {
  parse: (message: string, values: TOptions) => unknown;
  addLookupKeys: (keys: string[]) => string[];
} = {
  type: 'i18nFormat',
  parse: (message, values) => formatMessage(message, values),
  // Defined so i18next does not add `_plural` and `_context` suffixed keys to the lookup: a message is its one key.
  addLookupKeys: (keys) => keys,
};
